import { useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useI18n } from '../../context/I18nContext';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/effect-coverflow';
import { EffectCoverflow, Navigation } from 'swiper/modules';
import ImageOnlySkeletonLoader from '../ImageOnlySkeletonLoader';
const {
  DEFAULT_TEAM_MEMBERS,
  getBioValueForLanguage,
  getLocalizedTeamDisplayName,
  getLocalizedTeamRole,
} = require('../../lib/teamMembers');

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.14,
      delayChildren: 0.08,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.75, ease: [0.22, 1, 0.36, 1] },
  },
};

function getMemberInitials(name = '') {
  return (
    String(name || '?')
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .map((part) => part.charAt(0))
      .join('')
      .slice(0, 2)
      .toUpperCase() || '?'
  );
}

function normalizeLookup(value = '') {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

function getLocalizedTeamBio(member, index, language, memberProfiles = []) {
  const memberBio = getBioValueForLanguage(member?.bioI18n || {}, String(member?.bio || member?.description || ''), language);
  if (memberBio || !Array.isArray(memberProfiles) || memberProfiles.length === 0) return memberBio;

  const localizedRole = getLocalizedTeamRole(member, language);
  const targetRole = normalizeLookup(localizedRole);
  const rawRole = normalizeLookup(member?.role);
  const byRole = memberProfiles.find((entry) => {
    const entryRole = normalizeLookup(entry?.role);
    return entryRole === targetRole || entryRole === rawRole;
  });
  const byIndex = memberProfiles[index];
  return String(
    getBioValueForLanguage(byRole?.bioI18n || {}, byRole?.bio || byRole?.description || '', language) ||
      getBioValueForLanguage(byIndex?.bioI18n || {}, byIndex?.bio || byIndex?.description || '', language) ||
      '',
  ).trim();
}

export default function AboutTeamSection({ teamMembers = [] }) {
  const { t, language } = useI18n();
  const [activeIndex, setActiveIndex] = useState(0);
  const glowRef = useRef(null);
  const glowRafRef = useRef(null);
  const swiperRef = useRef(null);
  const prevRef = useRef(null);
  const nextRef = useRef(null);
  const memberProfiles = t('aboutPage.team.members', []);
  const resolvedTeamMembers =
    Array.isArray(teamMembers) && teamMembers.length > 0 ? teamMembers : DEFAULT_TEAM_MEMBERS;
  const slidesCount = resolvedTeamMembers.length;

  const handleMemberChange = (index) => {
    if (index === activeIndex) return;
    const swiper = swiperRef.current;
    if (!swiper) return;
    swiper.slideTo(index);
  };

  const activeMember = resolvedTeamMembers[activeIndex] || resolvedTeamMembers[0];
  const activeDisplayName = getLocalizedTeamDisplayName(activeMember, language);
  const activeRole = getLocalizedTeamRole(activeMember, language);
  const activeBio = getLocalizedTeamBio(activeMember, activeIndex, language, memberProfiles);

  const handleSectionMouseMove = (event) => {
    if (glowRafRef.current) return;
    const clientX = event.clientX;
    const clientY = event.clientY;
    const rect = event.currentTarget.getBoundingClientRect();
    glowRafRef.current = requestAnimationFrame(() => {
      glowRafRef.current = null;
      const x = Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100));
      const y = Math.max(0, Math.min(100, ((clientY - rect.top) / rect.height) * 100));
      if (glowRef.current) {
        glowRef.current.style.backgroundImage = `radial-gradient(circle at ${x}% ${y}%, rgba(255,93,16,0.10) 0%, transparent 42%)`;
      }
    });
  };

  return (
    <section
      className="relative bg-[var(--dash-blacker)] py-40 text-zinc-200"
      onMouseMove={handleSectionMouseMove}
    >
      <div className="absolute inset-0 opacity-20 bg-[url('/bg/5.webp')] bg-no-repeat bg-[size:100%_100%]" />
      <div className="pointer-events-none absolute inset-0 z-[1]">
        <div
          ref={glowRef}
          className="absolute left-0 top-0 h-full w-full transition-[background-image] duration-150 ease-out"
          style={{ backgroundImage: 'radial-gradient(circle at 50% 30%, rgba(255,93,16,0.10) 0%, transparent 42%)' }}
        />

      </div>
      <div className="container relative z-10 mx-auto px-6">
        <motion.div
          className="mb-14 text-center"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-120px' }}
        >
          
          <motion.h2 variants={itemVariants} className="text-4xl font-light uppercase leading-tight text-white sm:text-5xl">
            <span className="block">{t('aboutPage.team.titleLine1', t('aboutPage.team.title', ''))}</span>
            {String(t('aboutPage.team.titleLine2', '') || '').trim() ? (
              <span className="block font-black bg-gradient-to-r from-orange-400 via-[var(--dash-main)] to-orange-700 bg-clip-text text-transparent">
                {t('aboutPage.team.titleLine2')}
              </span>
            ) : null}
          </motion.h2>
          <motion.p className="mx-auto mt-5 max-w-2xl text-sm font-light leading-relaxed text-slate-400 sm:text-base lg:text-lg" variants={itemVariants}>
            {t('aboutPage.team.description')}
          </motion.p>
        </motion.div>

        <motion.div
          className="grid gap-12"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-120px' }}
        >
          <motion.div className="min-w-0 relative" variants={itemVariants}>
            <Swiper
              modules={[EffectCoverflow, Navigation]}
              navigation={{ prevEl: '.about-team-prev', nextEl: '.about-team-next' }}
              effect="coverflow"
              coverflowEffect={{
                rotate: 0,
                stretch: 0,
                depth: 100,
                modifier: 2,
                slideShadows: false,
              }}
              speed={900}
              grabCursor
              loop={true}
              watchSlidesProgress
              centeredSlides
              centerInsufficientSlides
              slidesPerView={3}
              spaceBetween={60}
              allowTouchMove={slidesCount > 1}
              breakpoints={{
                320: {
                  slidesPerView: 1,
                  spaceBetween: 16,
                },
                640: {
                  slidesPerView: 2,
                  spaceBetween: 24,
                },
                1024: {
                  slidesPerView: 3,
                  spaceBetween: 36,
                },
              }}
              onSwiper={(swiper) => {
                swiperRef.current = swiper;
                const idx = Number.isFinite(swiper.realIndex) ? swiper.realIndex : 0;
                setActiveIndex(idx);
                try {
                  if (prevRef.current && nextRef.current) {
                    swiper.params.navigation.prevEl = prevRef.current;
                    swiper.params.navigation.nextEl = nextRef.current;
                    if (swiper.navigation) {
                      swiper.navigation.destroy();
                      swiper.navigation.init();
                      swiper.navigation.update();
                    }
                  }
                } catch (e) {
                  /* ignore */
                }
              }}
              onSlideChange={(swiper) => {
                const idx = Number.isFinite(swiper.realIndex) ? swiper.realIndex : swiper.activeIndex;
                setActiveIndex(((idx % slidesCount) + slidesCount) % slidesCount);
              }}
              className="w-full overflow-visible [&_.swiper-wrapper]:h-auto [&_.swiper-wrapper]:overflow-visible [&_.swiper-slide]:h-auto [&_.swiper-slide]:overflow-visible [&_.swiper-slide-shadow]:hidden [&_.swiper-slide-shadow-left]:hidden [&_.swiper-slide-shadow-right]:hidden [&_.swiper-slide-shadow-top]:hidden [&_.swiper-slide-shadow-bottom]:hidden"
            >
              {resolvedTeamMembers.map((member) => {
                const displayName = getLocalizedTeamDisplayName(member, language);
                const role = getLocalizedTeamRole(member, language);
                const bio = getLocalizedTeamBio(member, 0, language, memberProfiles);
                return (
                  <SwiperSlide key={member._id || member.id || member.name} className="h-full">
                    {({ isActive }) => (
                      <div
                        className={`group flex h-full flex-col overflow-visible rounded-[36px] bg-zinc-950/90 text-white transition-all duration-300 bg-[linear-gradient(45deg,rgba(0,0,0,1)_30%,rgba(18,18,24,1)_70%)] ${
                          isActive
                            ? 'scale-100'
                            : 'scale-[0.98] opacity-100 filter blur-[2px]'
                        } hover:-translate-y-1`}
                      >
                          <div className="relative overflow-visible bg-slate-900">
                          {member.imageUrl ? (
                            <ImageOnlySkeletonLoader
                              src={member.imageUrl}
                              mode="native"
                              alt={displayName || member.name}
                              containerClassName="h-[26rem] w-full sm:h-[26rem] lg:h-[26rem]"
                              imgClassName="h-[26rem] w-full object-cover object-top transition-transform duration-700 transform sm:h-[26rem] lg:h-[26rem]"
                            />
                          ) : (
                            <div className="flex h-[22rem] w-full items-center justify-center bg-[linear-gradient(135deg,var(--dash-main),#111827)] text-6xl font-black text-white sm:h-[24rem] lg:h-[26rem] transform transition-transform duration-700 group-hover:translate-y-2">
                              {getMemberInitials(member.name)}
                            </div>
                          )}
                          <div className="absolute inset-0 bg-gradient-to-t from-slate-950/20 via-slate-950/8 to-transparent" />
                        </div>
                        <div className="flex flex-col gap-3 p-5">
                          <div>
                            <div className="flex items-start justify-between gap-4">
                              <div className="min-w-0">
                                <p className="text-xl font-semibold text-white">{displayName || member.name}</p>
                                    <div className="mt-2 flex items-start gap-3">
                                      <span className="inline-block h-2 w-2 mt-1 rounded-sm bg-[var(--dash-main)] shadow-sm flex-shrink-0" />
                                      <p className="text-sm font-semibold text-[var(--dash-main)] uppercase tracking-[0.12em]">{role}</p>
                                    </div>
                                  </div>
                            </div>
                          </div>
                          {bio ? (
                            <p className="text-sm leading-relaxed text-slate-300 line-clamp-4 mt-3">{bio}</p>
                          ) : null}
                        </div>
                      </div>
                    )}
                  </SwiperSlide>
                );
              })}
            </Swiper>
            <div className="relative mt-6">
              <div className="container mx-auto flex justify-center px-6 md:px-12 pb-4">
                <div className="flex items-center gap-4">
                  <button
                    ref={prevRef}
                    type="button"
                    aria-label="Previous"
                    className="about-team-prev group relative flex h-14 w-14 items-center justify-center overflow-hidden rounded-full"
                  >
                    <div className="absolute inset-0 scale-100 rounded-full bg-[var(--dash-main)] transition-all duration-700 group-hover:scale-150 group-hover:opacity-0" />
                    <div className="absolute inset-0 scale-0 rounded-full bg-[var(--dash-other)] transition-transform duration-500 group-hover:scale-100" />
                    <svg className="relative z-10 h-6 w-6 text-white group-hover:text-[var(--dash-black)] transition-colors" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </button>
                  <button
                    ref={nextRef}
                    type="button"
                    aria-label="Next"
                    className="about-team-next group relative flex h-14 w-14 items-center justify-center overflow-hidden rounded-full"
                  >
                    <div className="absolute inset-0 scale-100 rounded-full bg-[var(--dash-main)] transition-all duration-700 group-hover:scale-150 group-hover:opacity-0" />
                    <div className="absolute inset-0 scale-0 rounded-full bg-[var(--dash-other)] transition-transform duration-500 group-hover:scale-100" />
                    <svg className="relative z-10 h-6 w-6 text-white group-hover:text-[var(--dash-black)] transition-colors" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
